import { useState, useEffect } from 'react';
import { AlertCircle, Check, X, ChevronDown, Loader2, RefreshCw } from 'lucide-react';

interface CandidateOption {
  id: string;
  display_name: string;
}

interface ChangeNotification {
  id: string;
  change_type: 'auto_merge' | 'requires_action' | 'deactivated';
  origin_display_name: string;
  current_target_category_id: string | null;
  current_target_display_name: string | null;
  confidence: number | null;
  candidate_options: CandidateOption[];
  created_at: string;
  updated_at: string;
}

/**
 * Vises som modal/toast ved pålogging hvis kandidat har uleste kategori-varsler.
 * 3 flow-typer:
 *  - auto_merge: informativt varsel ("din interesse X heter nå Y — auto-tilpasset"), kandidat bekrefter
 *  - requires_action: forslag med confidence 0.70-0.89, kandidat velger fra dropdown
 *  - deactivated: kategori helt borte, kandidat velger ny fra dropdown eller ignorerer
 * Kjede-collapse (via UPSERT i backend) sikrer at bare siste tilstand vises.
 */
export function InterestChangeNotifications() {
  const [notifications, setNotifications] = useState<ChangeNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState<string | null>(null);
  const [selectedAlternative, setSelectedAlternative] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const res = await fetch('/api/candidate/interests/change-notifications', { credentials: 'include' });
      if (!res.ok) {
        setLoading(false);
        return; // 401 = ikke innlogget, ignorer stille
      }
      const data = await res.json();
      setNotifications(data.notifications || []);
    } catch (_) {
      // stille
    } finally {
      setLoading(false);
    }
  }

  async function resolve(notif: ChangeNotification, resolution: 'confirmed' | 'chose_alternative' | 'ignored') {
    setResolving(notif.id);
    setError(null);
    try {
      const body: any = { resolution };
      if (resolution === 'chose_alternative') {
        const targetId = selectedAlternative[notif.id];
        if (!targetId) {
          setError('Velg en kategori først');
          setResolving(null);
          return;
        }
        body.target_category_id = targetId;
      }
      const res = await fetch(`/api/candidate/interests/change-notifications/${notif.id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      // Fjern fra lista lokalt
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setResolving(null);
    }
  }

  if (loading || notifications.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl max-w-2xl w-full my-8 shadow-2xl">
        <header className="px-6 py-5 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-purple-600" />
            Oppdateringer i dine interesser
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Kurskatalogen på kompetanseheving.no har endret {notifications.length === 1 ? 'en av dine kategorier' : `${notifications.length} av dine kategorier`}. Gjennomgå og bekreft:
          </p>
        </header>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {notifications.map(notif => (
            <div key={notif.id} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              {notif.change_type === 'auto_merge' && notif.current_target_display_name && (
                <>
                  <div className="text-sm text-slate-700">
                    <span className="font-medium">«{notif.origin_display_name}»</span> heter nå
                    <span className="font-medium"> «{notif.current_target_display_name}»</span>.
                    Vi har auto-tilpasset — bekreft eller velg en annen kategori.
                  </div>
                  {notif.confidence != null && (
                    <div className="text-xs text-slate-500 mt-1">Sikkerhet: {(notif.confidence * 100).toFixed(0)}%</div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => resolve(notif, 'confirmed')}
                      disabled={resolving === notif.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {resolving === notif.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      Bekreft
                    </button>
                    {notif.candidate_options && notif.candidate_options.length > 0 && (
                      <AlternativePicker
                        notif={notif}
                        selectedAlternative={selectedAlternative}
                        setSelectedAlternative={setSelectedAlternative}
                        resolving={resolving}
                        onResolve={() => resolve(notif, 'chose_alternative')}
                      />
                    )}
                  </div>
                </>
              )}

              {notif.change_type === 'requires_action' && (
                <>
                  <div className="text-sm text-slate-700">
                    Kategorien <span className="font-medium">«{notif.origin_display_name}»</span> er endret i katalogen.
                    {notif.current_target_display_name && (
                      <> Vi foreslår <span className="font-medium">«{notif.current_target_display_name}»</span> — velg denne eller en annen.</>
                    )}
                  </div>
                  {notif.confidence != null && (
                    <div className="text-xs text-slate-500 mt-1">Sikkerhet på forslag: {(notif.confidence * 100).toFixed(0)}%</div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {notif.current_target_category_id && notif.current_target_display_name && (
                      <button
                        onClick={() => {
                          setSelectedAlternative(prev => ({ ...prev, [notif.id]: notif.current_target_category_id! }));
                          resolve({ ...notif, id: notif.id }, 'chose_alternative');
                        }}
                        disabled={resolving === notif.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        <Check className="h-4 w-4" />
                        Godta forslag: {notif.current_target_display_name}
                      </button>
                    )}
                    <AlternativePicker
                      notif={notif}
                      selectedAlternative={selectedAlternative}
                      setSelectedAlternative={setSelectedAlternative}
                      resolving={resolving}
                      onResolve={() => resolve(notif, 'chose_alternative')}
                    />
                    <button
                      onClick={() => resolve(notif, 'ignored')}
                      disabled={resolving === notif.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
                    >
                      <X className="h-4 w-4" /> Ignorer
                    </button>
                  </div>
                </>
              )}

              {notif.change_type === 'deactivated' && (
                <>
                  <div className="text-sm text-slate-700">
                    Kategorien <span className="font-medium">«{notif.origin_display_name}»</span> finnes ikke lenger i katalogen.
                    Ønsker du å velge en annen?
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <AlternativePicker
                      notif={notif}
                      selectedAlternative={selectedAlternative}
                      setSelectedAlternative={setSelectedAlternative}
                      resolving={resolving}
                      onResolve={() => resolve(notif, 'chose_alternative')}
                    />
                    <button
                      onClick={() => resolve(notif, 'ignored')}
                      disabled={resolving === notif.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
                    >
                      Fjern interessen
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AlternativePicker({
  notif, selectedAlternative, setSelectedAlternative, resolving, onResolve
}: {
  notif: ChangeNotification;
  selectedAlternative: Record<string, string>;
  setSelectedAlternative: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  resolving: string | null;
  onResolve: () => void;
}) {
  const opts = notif.candidate_options || [];
  if (opts.length === 0) return null;
  const chosen = selectedAlternative[notif.id];
  return (
    <div className="flex gap-2 items-center">
      <div className="relative">
        <select
          value={chosen || ''}
          onChange={e => setSelectedAlternative(prev => ({ ...prev, [notif.id]: e.target.value }))}
          className="pl-3 pr-8 py-1.5 text-sm border border-slate-300 rounded-md bg-white appearance-none"
        >
          <option value="">Velg annen kategori…</option>
          {opts.map(o => <option key={o.id} value={o.id}>{o.display_name}</option>)}
        </select>
        <ChevronDown className="h-4 w-4 absolute right-2 top-2 text-slate-400 pointer-events-none" />
      </div>
      <button
        onClick={onResolve}
        disabled={!chosen || resolving === notif.id}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:opacity-50"
      >
        {resolving === notif.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        Velg
      </button>
    </div>
  );
}
